import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export function useHousehold(user: User | null) {
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by retry() to force the effect below to run again without a
  // full page reload — App.tsx used to have no way to recover from a
  // failed initUser call other than silently falling back to the login
  // screen, which looked like the sign-in itself had failed even though
  // the user was still authenticated.
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    async function init() {
      if (!user) {
        setHouseholdId(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const initUserFn = httpsCallable<{ email: string }, { householdId: string }>(functions, 'initUser');
        // We pass email just in case, though the backend can read it from the auth token too
        const result = await initUserFn({ email: user.email || '' });
        setHouseholdId(result.data.householdId);
      } catch (err: any) {
        console.error('Failed to initialize user household:', err);
        setError(err.message || 'Failed to load household');
      } finally {
        setLoading(false);
      }
    }

    init();
  }, [user, retryCount]);

  const retry = () => setRetryCount(c => c + 1);

  return { householdId, loading, error, retry };
}
